import React, { memo, useState, useRef, useCallback, useContext, useEffect } from "react";
import { TextField, Autocomplete } from "@mui/material";
import styled from "styled-components";
import { getSiteRefs } from "../../services/api/siteService";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import ClearIcon from "@mui/icons-material/Clear";
import { InputAdornment, IconButton } from '@mui/material';

const StyledAutocomplete = styled(Autocomplete)`
  text-align: left;
  width: 100%;

  & .MuiAutocomplete-inputRoot {
    height: 30px;
    padding: 2px 0px 2px 8px;
  }
  & .MuiAutocomplete-input {
    height: 0px;
    font: normal normal 550 14px/19px Open Sans;
    letter-spacing: 0px;
    color: #707070;
    opacity: 1;
    align-items: center;
    text-transform: capitalize;
  }
`;

const InfiniteDropdown = ({
  control,
  setControl,
  appliedFilters,
  siteRefs,
  setSiteRefs,
  setSiteRefsDetails
}) => {

  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isFullDataLoad, setIsFullDataLoad] = useState(false);
  const { edotcoSiteAreaToggle, selectedSiteRef } = useContext(WebMapContext);

  useEffect(() => {
    if (selectedSiteRef.length === 0 && inputValue && siteRefs?.length === 1) {
      setInputValue("");
      loadResults(1);
    }
  }, [selectedSiteRef])

  const loadResults = useCallback(async (pageNum, search = "") => {
    try {
      let queryParams = {
        pageNum: pageNum,
        countryCode: appliedFilters.countryCode,
        quarter: appliedFilters.quarter,
        year: appliedFilters.year,
        adL: edotcoSiteAreaToggle && appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
        adLVal: edotcoSiteAreaToggle && appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null,
        ...(search && { searchKey: search?.trim() })
      };

      const response = await getSiteRefs(queryParams);
      if (response?.data?.length > 0) {
        let siteRefsArr = response?.data?.map((item) => item?.siteRef);
        setSiteRefs((prevOptions) => pageNum === 1 ? [...siteRefsArr] : [...prevOptions, ...siteRefsArr]);
        setPage(pageNum);
        setSiteRefsDetails(prevSiteRefsDetails => [...prevSiteRefsDetails, ...response?.data]);
        setIsFullDataLoad(false);
      } else {
        setIsFullDataLoad(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [appliedFilters.countryCode, appliedFilters.quarter, appliedFilters.year, appliedFilters.level, appliedFilters.levelVal, page, inputValue, edotcoSiteAreaToggle]);

  const handleChange = (event, newValue) => {
    setControl(newValue);
    if (newValue) {
      setInputValue(newValue);
    } else {
      setInputValue("");
      loadResults(1);
    }
  };
  
  const handleInputChange = async (event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.trim() !== "") loadResults(1, newInputValue);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleScroll = useCallback(() => {
    const scrollTop = listRef.current.scrollTop;
    const scrollHeight = listRef.current.scrollHeight;
    const clientHeight = listRef.current.clientHeight;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !isFullDataLoad) {
      loadResults(page + 1, inputValue);
    }
  }, [loadResults, isFullDataLoad]);

  const formattedOptions = siteRefs.map((option) => ({ label: option }));

  return (
    <>
      <StyledAutocomplete
        value={control || ""}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        id="type-select"
        options={siteRefs}
        autoHighlight
        getOptionLabel={(option) => {
          const formattedOption = formattedOptions.find(
            (formatted) => formatted.label === option
          );
          return formattedOption ? formattedOption.label : "";
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              style: { textTransform: "none" }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: control && (
                <InputAdornment position="end">
                  <IconButton style={{
                    cursor: "pointer",
                    position: "absolute",
                    right: "14px",
                    color: "rgba(0,0,0,0.54)"
                  }} edge="end" onClick={(e) => handleChange(e, null)}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: "200px",
            overflow: "auto",
          },
          ref: listRef,
          onScroll: handleScroll,
        }}
      />

    </>
  );
};

export default memo(InfiniteDropdown);
